"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const TARGET_IDS = [11, 12, 15, 13];

const INITIAL_TOP_SELLERS = [
  {
    id: 11,
    title: "Lexus-LX600",
    tags: ["لکسوس", "شاسی بلند", "بنزینی"],
    href: "/products/11",
    imageSrc: "/section3/LX600/LX600.webp",
    videoSrc: "/section3/LX600/LX600.mp4",
  },
  {
    id: 12,
    title: "Land Cruiser",
    tags: ["تویوتا", "شاسی بلند", "بنزینی"],
    href: "/products/12",
    imageSrc: "/section3/Landcruiser/Landcruiser.webp",
    videoSrc: "section3/Landcruiser/Landcruiser.mp4",
  },
  {
    id: 15,
    title: "KIA Sportage",
    tags: ["کیا", "کراس اوور", "بنزینی"],
    href: "/products/15",
    imageSrc: "/section3/Sportage/sportage.webp",
    videoSrc: "/section3/Sportage/sportage.mp4",
  },
  {
    id: 13,
    title: "Prado",
    tags: ["تویوتا", "شاسی بلند", "بنزینی"],
    href: "/products/13",
    imageSrc: "/section3/Prado/Prado.webp",
    videoSrc: "/section3/Prado/Prado.mp4",
  },
];

function SellerCard({ car }) {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <Link
      href={car.href}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleMouseEnter}
      onTouchEnd={handleMouseLeave}
      onTouchCancel={handleMouseLeave}
      className="group relative w-full h-full rounded-card-mob md:rounded-card-desk border border-white/10 overflow-hidden bg-black shadow-2xl flex flex-col justify-between p-6 md:p-8 transition-all duration-500 block"
    >
      {/* Cover Background Image */}
      <Image
        src={car.imageSrc}
        alt={car.title}
        fill
        sizes="(max-width: 768px) 100vw, 50vw"
        className={`absolute inset-0 object-cover transition-opacity duration-700 ${isHovered ? "opacity-0" : "opacity-100"
          }`}
      />

      {/* Hover Background Video */}
      <video
        ref={videoRef}
        src={car.videoSrc}
        muted
        loop
        playsInline
        preload="none"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isHovered ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
      />

      {/* 80% Linear Dark Gradient Overlay */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.1) 65%, rgba(0,0,0,0.85) 100%)" }}
      />

      {/* Center of Card: Large Series / Car Name */}
      <div className="relative z-10 w-full flex items-start justify-center text-center px-4 pt-4">
        <h3 className="text-[30px] sm:text-[40px] md:text-[50px] lg:text-[70px] font-anjoman-thin text-white tracking-wide  drop-shadow-md">
          {car.title}
        </h3>
      </div>

      {/* Bottom Bar: Tags on Right, Left Chevron on Left */}
      <div className="relative z-10 w-full flex justify-between items-center text-white" dir="rtl">
        {/* Right side tags: Brand | Class | Fuel */}
        <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm font-anjoman-thin text-gray-300">
          {car.tags.map((tag, idx) => (
            <span key={idx}>{tag}</span>
          ))}
        </div>

        {/* Left Chevron */}
        <div className="text-gray-400 group-hover:text-white transition-colors">
          <FaChevronLeft className="text-xs md:text-sm group-hover:-translate-x-1 transition-transform duration-300" />
        </div>
      </div>
    </Link>
  );
}

function DesktopRow({ car1, car2 }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const getWidthClass = (index) => {
    if (hoveredIdx === null) return "w-1/2";
    return hoveredIdx === index ? "w-[60%]" : "w-[40%]";
  };

  return (
    <div className="flex w-full gap-6 h-[460px] md:h-[550px] lg:h-[640px]">
      <div
        onMouseEnter={() => setHoveredIdx(0)}
        onMouseLeave={() => setHoveredIdx(null)}
        className={`h-full transition-[width] duration-500 ease-out ${getWidthClass(0)}`}
      >
        <SellerCard car={car1} />
      </div>
      <div
        onMouseEnter={() => setHoveredIdx(1)}
        onMouseLeave={() => setHoveredIdx(null)}
        className={`h-full transition-[width] duration-500 ease-out ${getWidthClass(1)}`}
      >
        <SellerCard car={car2} />
      </div>
    </div>
  );
}

function MobileSlider({ cars }) {
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [activeIndex, setActiveIndex] = useState(1);

  return (
    <div className="flex flex-col items-center w-full gap-4 pt-2 pb-4" dir="ltr">
      {/* Tall Cards Swiper matching Figma aspect ratio */}
      <div className="w-full h-[72vh]  max-h-[670px]">
        <Swiper
          onSwiper={setSwiperInstance}
          onSlideChange={(s) => setActiveIndex(s.activeIndex)}
          spaceBetween={14}
          slidesPerView={1.12}
          centeredSlides={true}
          initialSlide={1}
          className="w-full h-full [&_.swiper-wrapper]:h-full [&_.swiper-slide]:h-full"
        >
          {cars.map((car) => (
            <SwiperSlide key={car.id} className="!h-full">
              <SellerCard car={car} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Mobile Navigation Bar matching Figma Sample (< • • ⦿ • >) positioned directly below cards */}
      <div className="flex items-center justify-center gap-3 pt-2 text-white">
        {/* Left Arrow Button */}
        <button
          onClick={() => swiperInstance?.slidePrev()}
          className="w-7 h-7 rounded-full border border-white/30 flex items-center justify-center text-white/70 hover:text-white hover:border-white transition-all cursor-pointer bg-black/20 backdrop-blur-xs"
          aria-label="اسلاید قبلی"
        >
          <FaChevronLeft className="text-[10px]" />
        </button>

        {/* Custom Pagination Dots */}
        <div className="flex items-center gap-2 px-1">
          {cars.map((_, idx) => (
            <button
              key={idx}
              onClick={() => swiperInstance?.slideTo(idx)}
              className="cursor-pointer transition-all duration-300 flex items-center justify-center p-1"
              aria-label={`اسلاید ${idx + 1}`}
            >
              {activeIndex === idx ? (
                /* Active Ring Dot matching Figma sample */
                <span className="w-3.5 h-3.5 rounded-full border border-white flex items-center justify-center">
                  <span className="w-1 h-1 bg-white rounded-full" />
                </span>
              ) : (
                /* Inactive Dot */
                <span className="w-1.5 h-1.5 bg-white/40 rounded-full hover:bg-white/70" />
              )}
            </button>
          ))}
        </div>

        {/* Right Arrow Button */}
        <button
          onClick={() => swiperInstance?.slideNext()}
          className="w-7 h-7 rounded-full border border-white/30 flex items-center justify-center text-white/70 hover:text-white hover:border-white transition-all cursor-pointer bg-black/20 backdrop-blur-xs"
          aria-label="اسلاید بعدی"
        >
          <FaChevronRight className="text-[10px]" />
        </button>
      </div>
    </div>
  );
}

export default function TopSellers({ onThemeChange }) {
  const [isBlackBg, setIsBlackBg] = useState(false);
  const [topSellers, setTopSellers] = useState(INITIAL_TOP_SELLERS);
  const sectionRef = useRef(null);

  const triggerThemeChange = useCallback((isDark) => {
    onThemeChange?.(isDark);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("topSellersThemeChange", { detail: { isDark } }));
    }
  }, [onThemeChange]);

  useEffect(() => {
    let leaveTimer = null;

    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // In view check for immediate dark component
      const isInView = rect.top <= windowHeight * 0.5 && rect.bottom > windowHeight * 0.1;

      if (isInView) {
        if (leaveTimer) {
          clearTimeout(leaveTimer);
          leaveTimer = null;
        }
        setIsBlackBg(true);
        triggerThemeChange(true);
      } else {
        if (!leaveTimer) {
          leaveTimer = setTimeout(() => {
            setIsBlackBg(false);
            triggerThemeChange(false);
          }, 500); // restored 500ms delay when switching back to white
        }
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (leaveTimer) {
            clearTimeout(leaveTimer);
            leaveTimer = null;
          }
          setIsBlackBg(true);
          triggerThemeChange(true);
        } else {
          if (!leaveTimer) {
            leaveTimer = setTimeout(() => {
              setIsBlackBg(false);
              triggerThemeChange(false);
            }, 500); // restored 500ms delay when switching back to white
          }
        }
      },
      { rootMargin: "-10% 0px -50% 0px", threshold: 0 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      if (leaveTimer) clearTimeout(leaveTimer);
    };
  }, [triggerThemeChange]);


  return (
    <section
      ref={sectionRef}
      className={`w-full transition-colors duration-[1200ms] ease-in-out py-8 md:py-20 ${isBlackBg ? "bg-[#050505] text-white" : "bg-white text-gray-900"
        }`}
    >
      <div className="max-w-[1920px] mx-auto px-4 md:px-12 lg:px-16 flex flex-col w-full gap-6">

        {/* Section Header: 'فراتر از حرکت' */}
        <div className="flex justify-center md:justify-start items-center text-center pt-2 md:pt-0" dir="rtl">
          <h2 className={`text-4xl md:text-5xl lg:text-6xl tracking-wide font-leila transition-colors duration-[1200ms] ease-in-out ${isBlackBg ? "text-white" : "text-gray-900"}`}>
            فراتر از حرکت
          </h2>
        </div>

        {/* Desktop & Tablet View */}
        <div className="hidden md:flex flex-col gap-6 w-full">
          <DesktopRow car1={topSellers[0]} car2={topSellers[1]} />
          <DesktopRow car1={topSellers[2]} car2={topSellers[3]} />
        </div>

        {/* Mobile View */}
        <div className="block md:hidden w-full">
          <MobileSlider cars={topSellers} />
        </div>

      </div>
    </section>
  );
}
